import { useEffect, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip, GeoJSON } from "react-leaflet";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { MapPin, Plus, Pencil, Trash2, Target, MessageSquarePlus, X, Mail } from "lucide-react";

const EMPTY = { name: "", address: "", referenti: [{ nome: "", telefono: "", email: "", riceve_mail: true }], note: "", esito: "", province: [] };

function ProvinceSelector({ value, onChange }) {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/geo/provinces/list").then((r) => setList(r.data || [])).catch(() => {}); }, []);
  const add = (name) => { if (name && !value.includes(name)) onChange([...value, name]); };
  const remove = (name) => onChange(value.filter((v) => v !== name));
  const available = list.filter((p) => !value.includes(p.name));
  return (
    <div className="space-y-2" data-testid="pot-province-selector">
      <div className="text-xs font-semibold uppercase text-muted-foreground">Aree di presidio · province *</div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((p) => (
            <span key={p} className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary" data-testid={`pot-prov-chip-${p}`}>
              {p}
              <button type="button" onClick={() => remove(p)} className="hover:text-red-500" data-testid={`pot-prov-remove-${p}`}><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
      <Select value="" onValueChange={add}>
        <SelectTrigger data-testid="pot-prov-select"><SelectValue placeholder="Seleziona provincia da elenco…" /></SelectTrigger>
        <SelectContent className="max-h-[260px]">
          {available.map((p) => <SelectItem key={p.acr} value={p.name} data-testid={`pot-prov-opt-${p.acr}`}>{p.name} ({p.acr})</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function FormDialog({ open, onClose, onSaved, initial }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [addrSug, setAddrSug] = useState([]);
  const [addrOpen, setAddrOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const src = (initial?.referenti && initial.referenti.length)
      ? initial.referenti
      : (initial ? [{ nome: initial.referente_nome || "", telefono: initial.referente_telefono || "", email: initial.referente_email || "", riceve_mail: true }] : EMPTY.referenti);
    const referenti = src.map((r) => ({ nome: r.nome || "", telefono: r.telefono || "", email: r.email || "", riceve_mail: r.riceve_mail !== false }));
    setF({ ...EMPTY, ...(initial || {}), referenti, province: (initial?.province) || [] });
  }, [open, initial]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setRef = (i, k, v) => setF((p) => ({ ...p, referenti: p.referenti.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)) }));
  const addRef = () => setF((p) => ({ ...p, referenti: [...p.referenti, { nome: "", telefono: "", email: "", riceve_mail: false }] }));
  const removeRef = (i) => setF((p) => ({ ...p, referenti: p.referenti.filter((_, idx) => idx !== i) }));
  const save = async () => {
    if (!String(f.name || "").trim()) { toast.error("Campo obbligatorio: Nome agenzia"); return; }
    if (!String(f.address || "").trim()) { toast.error("Campo obbligatorio: Indirizzo"); return; }
    const refs = (f.referenti || []).map((r) => ({ nome: (r.nome || "").trim(), telefono: (r.telefono || "").trim(), email: (r.email || "").trim(), riceve_mail: !!r.riceve_mail }));
    for (const r of refs) { if ((r.nome || r.telefono || r.email) && !(r.nome && r.telefono && r.email)) { toast.error("Completa nome, telefono ed email di ogni referente"); return; } }
    const complete = refs.filter((r) => r.nome && r.telefono && r.email);
    if (!complete.length) { toast.error("Inserisci almeno un referente completo"); return; }
    if (!complete.some((r) => r.riceve_mail)) { toast.error("Spunta almeno un referente che riceverà la mail"); return; }
    if (!String(f.note || "").trim()) { toast.error("Campo obbligatorio: Note"); return; }
    if (!(f.province && f.province.length)) { toast.error("Seleziona almeno una provincia di presidio"); return; }
    if (f.esito !== "positivo" && f.esito !== "negativo") { toast.error("Seleziona l'esito"); return; }
    setBusy(true);
    try {
      const payload = { ...f, referenti: complete };
      if (initial?.id) await api.put(`/potential-agencies/${initial.id}`, payload);
      else await api.post(`/potential-agencies`, payload);
      toast.success(initial?.id ? "Agenzia aggiornata" : "Agenzia potenziale aggiunta");
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nel salvataggio"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="potenziale-form-dialog">
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Modifica agenzia potenziale" : "Nuova agenzia potenziale"}</DialogTitle>
          <DialogDescription>Dati isolati in questo modulo: non alimentano altri archivi. L'indirizzo viene geolocalizzato per la mappa. <b>Tutti i campi sono obbligatori.</b></DialogDescription>
        </DialogHeader>
        <div className="max-h-[70vh] space-y-3 overflow-y-auto pr-1">
          <Input placeholder="Nome agenzia *" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="pot-name" />
          <div className="relative">
            <Input placeholder="Indirizzo (via, città) *" value={f.address} autoComplete="off" data-testid="pot-address"
              onChange={(e) => {
                const q = e.target.value; set("address", q); set("lat", null); set("lng", null);
                if (q.trim().length >= 3) { api.get(`/geocode/suggest?q=${encodeURIComponent(q.trim())}`).then((r) => { setAddrSug(r.data?.suggestions || []); setAddrOpen(true); }).catch(() => setAddrSug([])); }
                else { setAddrSug([]); setAddrOpen(false); }
              }} />
            {addrOpen && addrSug.length > 0 && (
              <div className="absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-md border border-border bg-popover shadow-lg" data-testid="pot-address-suggestions">
                {addrSug.map((s, i) => (
                  <button type="button" key={i} data-testid={`pot-address-sug-${i}`}
                    onClick={() => { set("address", s.label); set("lat", s.lat); set("lng", s.lng); setAddrOpen(false); setAddrSug([]); }}
                    className="block w-full truncate px-3 py-2 text-left text-xs hover:bg-accent">{s.label}</button>
                ))}
              </div>
            )}
            {f.lat != null && f.lng != null && <div className="mt-1 text-[11px] font-semibold text-emerald-500" data-testid="pot-address-geo-ok">✓ Posizione individuata sulla mappa</div>}
          </div>
          <div className="space-y-2" data-testid="pot-referenti">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase text-muted-foreground">Referenti * — spunta chi riceve la mail</span>
              <Button type="button" size="sm" variant="outline" onClick={addRef} data-testid="pot-ref-add"><Plus className="mr-1 h-3.5 w-3.5" />Referente</Button>
            </div>
            {(f.referenti || []).map((r, i) => (
              <div key={i} className="space-y-2 rounded-md border border-border p-2" data-testid={`pot-ref-${i}`}>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  <Input placeholder="Nome *" value={r.nome} onChange={(e) => setRef(i, "nome", e.target.value)} data-testid={`pot-ref-nome-${i}`} />
                  <Input placeholder="Telefono *" value={r.telefono} onChange={(e) => setRef(i, "telefono", e.target.value)} data-testid={`pot-ref-tel-${i}`} />
                  <Input placeholder="Email *" value={r.email} onChange={(e) => setRef(i, "email", e.target.value)} data-testid={`pot-ref-email-${i}`} />
                </div>
                <div className="flex items-center justify-between">
                  <button type="button" onClick={() => setRef(i, "riceve_mail", !r.riceve_mail)} data-testid={`pot-ref-flag-${i}`}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${r.riceve_mail ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}>
                    <Mail className="h-3.5 w-3.5" />{r.riceve_mail ? "Riceve la mail" : "Non riceve"}
                  </button>
                  {(f.referenti || []).length > 1 && (
                    <button type="button" onClick={() => removeRef(i)} data-testid={`pot-ref-remove-${i}`} className="text-muted-foreground hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <ProvinceSelector value={f.province || []} onChange={(v) => set("province", v)} />
          <Textarea placeholder="Note sulla chiamata *" value={f.note} onChange={(e) => set("note", e.target.value)} rows={3} data-testid="pot-note" />
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase text-muted-foreground">Esito *</span>
            <button type="button" onClick={() => set("esito", "positivo")} data-testid="pot-esito-positivo"
              className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${f.esito === "positivo" ? "border-green-500 bg-green-500 text-white" : "border-border text-muted-foreground hover:border-green-500/50"}`}>Positivo</button>
            <button type="button" onClick={() => set("esito", "negativo")} data-testid="pot-esito-negativo"
              className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${f.esito === "negativo" ? "border-red-500 bg-red-500 text-white" : "border-border text-muted-foreground hover:border-red-500/50"}`}>Negativo</button>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button size="sm" variant="ghost" onClick={onClose} data-testid="pot-cancel">Annulla</Button>
            <Button size="sm" onClick={save} disabled={busy} data-testid="pot-save">{busy ? "…" : "Salva"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NoteDialog({ agency, onClose, onSaved }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setText(""); }, [agency]);
  const save = async () => {
    if (!text.trim()) { toast.error("Nota vuota"); return; }
    setBusy(true);
    try {
      await api.post(`/potential-agencies/${agency.id}/notes`, { text });
      toast.success("Nota aggiunta");
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!agency} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="pot-note-dialog">
        <DialogHeader>
          <DialogTitle>Aggiungi nota · {agency?.name}</DialogTitle>
          <DialogDescription>La nota si aggiunge allo storico dell'agenzia ed è visibile cliccando la posizione sulla mappa.</DialogDescription>
        </DialogHeader>
        <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrivi la nota…" data-testid="pot-note-textarea" />
        <div className="flex justify-end gap-2 pt-2">
          <Button size="sm" variant="ghost" onClick={onClose}>Annulla</Button>
          <Button size="sm" onClick={save} disabled={busy} data-testid="pot-note-save">{busy ? "…" : "Salva nota"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MailDialog({ agency, onClose, onSent }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!agency) { setData(null); return; }
    setData(null);
    api.get(`/potential-agencies/${agency.id}/intro-email/preview`)
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(e?.response?.data?.detail || "Impossibile preparare la mail"); onClose(); });
  }, [agency]);
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/potential-agencies/${agency.id}/send-intro-email`, { subject: data.subject, body: data.body });
      toast.success(`Mail inviata a ${r.data.to}${r.data.cc?.length ? ` (CC: ${r.data.cc.length})` : ""}`);
      onSent && onSent();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invio non riuscito"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!agency} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="pot-mail-dialog">
        <DialogHeader>
          <DialogTitle>Invia mail al referente · {agency?.name}</DialogTitle>
          <DialogDescription>Mittente: Responsabile Commerciale · In CC: Admin e Head of Partner. Puoi modificare oggetto e testo prima dell'invio.</DialogDescription>
        </DialogHeader>
        {!data ? <div className="py-8 text-center text-sm text-muted-foreground">Preparazione…</div> : (
          <div className="space-y-3">
            <div className="rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground" data-testid="pot-mail-recipients">
              <b>A:</b> {data.to}{data.cc?.length ? <span> · <b>CC:</b> {data.cc.join(", ")}</span> : null}
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold uppercase text-muted-foreground">Oggetto</label>
              <Input value={data.subject} onChange={(e) => setData({ ...data, subject: e.target.value })} data-testid="pot-mail-subject" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold uppercase text-muted-foreground">Testo della mail (modificabile)</label>
              <Textarea rows={14} value={data.body} onChange={(e) => setData({ ...data, body: e.target.value })} data-testid="pot-mail-body" className="text-sm" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button size="sm" variant="ghost" onClick={onClose}>Annulla</Button>
              <Button size="sm" onClick={send} disabled={busy || !data.subject.trim() || !data.body.trim()} data-testid="pot-mail-send">{busy ? "Invio…" : "Invia mail"}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


export default function PotenzialiAgenzie() {
  const [data, setData] = useState({ items: [], settimana_count: 0, settimana_target: 4 });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [noteFor, setNoteFor] = useState(null);
  const [mailFor, setMailFor] = useState(null);
  const [selected, setSelected] = useState(null);
  const [provGeo, setProvGeo] = useState(null);
  const [recomputing, setRecomputing] = useState(false);

  const load = () => api.get(`/potential-agencies`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const selectAgency = (a) => {
    setSelected(a);
    setProvGeo(null);
    if (a.province && a.province.length) {
      api.get(`/geo/provinces?names=${encodeURIComponent(a.province.join(","))}`).then((r) => setProvGeo(r.data)).catch(() => setProvGeo(null));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questa agenzia potenziale?")) return;
    try { await api.delete(`/potential-agencies/${id}`); toast.success("Eliminata"); if (selected?.id === id) { setSelected(null); setProvGeo(null); } load(); }
    catch { toast.error("Errore nell'eliminazione"); }
  };

  const openMail = (a) => {
    const refs = (a.referenti && a.referenti.length) ? a.referenti : [{ email: a.referente_email }];
    if (!refs.some((r) => (r.email || "").includes("@"))) { toast.error("Email referente mancante: modifica l'agenzia e aggiungila."); return; }
    setMailFor(a);
  };

  const withGeo = (data.items || []).filter((x) => x.lat != null && x.lng != null);
  const target = data.settimana_target || 4;
  const cnt = data.settimana_count || 0;
  const ok = cnt >= target;
  const selCol = selected?.esito === "positivo" ? "#22c55e" : "#ef4444";
  const allNotes = (a) => [...(a.note ? [{ text: a.note }] : []), ...((a.notes) || [])];

  return (
    <Card className="p-6" data-testid="potenziali-agenzie-card">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-primary" />
          <h2 className="font-heading text-lg font-bold tracking-tight">Agenzie potenziali</h2>
        </div>
        <div className="flex items-center gap-3">
          <div className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${ok ? "border-green-500/40 bg-green-500/10 text-green-500" : "border-amber-500/40 bg-amber-500/10 text-amber-500"}`} data-testid="pot-weekly-counter">
            <Target className="h-3.5 w-3.5" /> Contatti questa settimana: {cnt}/{target}
          </div>
          <Button size="sm" variant="outline" disabled={recomputing} data-testid="pot-recompute-geo"
            onClick={async () => { setRecomputing(true); try { const r = await api.post(`/potential-agencies/recompute-geo`); toast.success(r.data?.message || "Geolocalizzazione ricalcolata"); load(); } catch { toast.error("Errore nel ricalcolo geolocalizzazione"); } finally { setRecomputing(false); } }}>
            <MapPin className="mr-1 h-4 w-4" />{recomputing ? "Ricalcolo…" : "Ricalcola geo"}</Button>
          <Button size="sm" onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="pot-add-btn"><Plus className="mr-1 h-4 w-4" />Aggiungi</Button>
        </div>
      </div>

      <div className="h-[360px] w-full overflow-hidden rounded-lg border border-border" data-testid="pot-map">
        <MapContainer center={[42.5, 12.5]} zoom={5} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
          <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {provGeo && <GeoJSON key={`${selected?.id}-${(selected?.province || []).join(",")}`} data={provGeo}
            style={() => ({ color: selCol, weight: 2, fillColor: selCol, fillOpacity: 0.22 })} />}
          {withGeo.map((a) => {
            const green = a.esito === "positivo";
            const col = green ? "#22c55e" : "#ef4444";
            const notes = allNotes(a);
            return (
              <CircleMarker key={a.id} center={[a.lat, a.lng]} radius={selected?.id === a.id ? 11 : 8}
                pathOptions={{ color: col, fillColor: col, fillOpacity: a.geo_precise === false ? 0.3 : 0.85, weight: selected?.id === a.id ? 4 : 2, dashArray: a.geo_precise === false ? "4 3" : undefined }}
                eventHandlers={{ click: () => selectAgency(a) }}>
                <Tooltip>{a.name} · {green ? "Positivo" : "Negativo"}{a.geo_precise === false ? " · pos. indicativa" : ""}</Tooltip>
                <Popup>
                  <div className="text-sm" data-testid={`pot-popup-${a.id}`}>
                    <div className="font-bold">{a.name}</div>
                    {a.address && <div className="text-xs">{a.address}</div>}
                    {a.geo_precise === false && <div className="text-[11px] font-semibold text-amber-600">⚠ Posizione indicativa (centro provincia)</div>}
                    {a.referente_nome && <div className="mt-1 text-xs">Ref: {a.referente_nome} {a.referente_telefono ? `· ${a.referente_telefono}` : ""}</div>}
                    {a.province && a.province.length > 0 && (
                      <div className="mt-1 text-xs"><b>Province di presidio:</b> {a.province.join(", ")}</div>
                    )}
                    {notes.length > 0 && (
                      <div className="mt-1 border-t border-black/10 pt-1">
                        <div className="text-[11px] font-semibold">Note ({notes.length}):</div>
                        <ul className="mt-0.5 space-y-0.5">
                          {notes.map((n, i) => (
                            <li key={i} className="text-xs">• {n.text}{n.at ? <span className="text-[10px] text-gray-500"> ({n.at.slice(0, 10)})</span> : null}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div className="mt-1 text-xs font-semibold" style={{ color: col }}>{green ? "Esito positivo" : "Esito negativo"}</div>
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </div>

      <div className="mt-4 space-y-2" data-testid="pot-list">
        {(data.items || []).length === 0 && <div className="py-6 text-center text-sm text-muted-foreground">Nessuna agenzia potenziale inserita.</div>}
        {(data.items || []).map((a) => {
          const nCount = allNotes(a).length;
          return (
            <div key={a.id} className={`flex items-center gap-3 rounded-md border px-3 py-2 transition-colors ${selected?.id === a.id ? "border-primary bg-primary/5" : "border-border"}`} data-testid={`pot-row-${a.id}`}>
              <span className={`h-3 w-3 shrink-0 rounded-full ${a.esito === "positivo" ? "bg-green-500" : "bg-red-500"}`} />
              <button type="button" onClick={() => a.lat != null && selectAgency(a)} className="min-w-0 flex-1 text-left" data-testid={`pot-select-${a.id}`}>
                <div className="truncate text-sm font-semibold">{a.name}</div>
                <div className="truncate text-[11px] text-muted-foreground">
                  {[a.address, a.referente_nome].filter(Boolean).join(" · ") || "—"}
                  {a.province && a.province.length > 0 ? ` · ${a.province.length} prov.` : ""}
                  {nCount > 0 ? ` · ${nCount} note` : ""}
                  {a.lat == null ? " · (indirizzo non localizzato)" : ""}
                </div>
              </button>
              <button onClick={() => openMail(a)} data-testid={`pot-mail-${a.id}`} title="Invia mail di presentazione al referente" className="text-muted-foreground hover:text-primary"><Mail className="h-4 w-4" /></button>
              <button onClick={() => setNoteFor(a)} data-testid={`pot-addnote-${a.id}`} title="Aggiungi nota" className="text-muted-foreground hover:text-primary"><MessageSquarePlus className="h-4 w-4" /></button>
              <button onClick={() => { setEditing(a); setFormOpen(true); }} data-testid={`pot-edit-${a.id}`} className="text-muted-foreground hover:text-primary"><Pencil className="h-4 w-4" /></button>
              <button onClick={() => del(a.id)} data-testid={`pot-del-${a.id}`} className="text-muted-foreground hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          );
        })}
      </div>

      <FormDialog open={formOpen} onClose={() => setFormOpen(false)} onSaved={load} initial={editing} />
      <NoteDialog agency={noteFor} onClose={() => setNoteFor(null)} onSaved={load} />
      <MailDialog agency={mailFor} onClose={() => setMailFor(null)} onSent={load} />
    </Card>
  );
}
